// @ts-check
import { useState, useEffect, useRef, useCallback } from "react"
import { useSelector } from "react-redux"
import { X, Search, Loader2, AlertCircle, RefreshCw } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { searchJournals } from "@/services/chat-provider/journals"

/**
 * @typedef {import("@/services/chat-provider/journals").JournalResult} JournalResult
 */

/**
 * @param {{
 *   open: boolean
 *   onClose: () => void
 *   onSubmit: (payload: { files: any[], journals: JournalResult[], additionalContext: string }) => void
 *   stagedFiles: any[]
 * }} props
 */
export default function GenerateReviewDialog({ open, onClose, onSubmit, stagedFiles }) {
  const { user } = useSelector((/** @type {any} */ state) => state.app.datastate)
  const userToken = user?.userToken ?? null

  // ── Internal state ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState(/** @type {JournalResult[]} */ ([]))
  const [selectedJournals, setSelectedJournals] = useState(/** @type {JournalResult[]} */ ([]))
  const [additionalContext, setAdditionalContext] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState(/** @type {string | null} */ (null))
  /** @type {import("react").MutableRefObject<"empty" | "results" | "error" | null>} */
  const lastSearchStateRef = useRef(null)

  const debounceTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null))
  const abortControllerRef = useRef(/** @type {AbortController | null} */ (null))

  // Reset internal state when dialog opens (but not stagedFiles — those come from props)
  useEffect(() => {
    if (open) {
      setSearchQuery("")
      setSearchResults([])
      setSelectedJournals([])
      setAdditionalContext("")
      setIsSearching(false)
      setSearchError(null)
      lastSearchStateRef.current = null
    }
  }, [open])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      abortControllerRef.current?.abort()
    }
  }, [])

  // ── Journal search with 400 ms debounce ────────────────────────────────────
  const executeSearch = useCallback(
    async (query) => {
      abortControllerRef.current?.abort()
      const thisController = new AbortController()
      abortControllerRef.current = thisController

      setIsSearching(true)
      setSearchError(null)
      setSearchResults([])
      lastSearchStateRef.current = null

      try {
        const results = await searchJournals(query, userToken, { signal: thisController.signal })
        if (thisController !== abortControllerRef.current) return
        setSearchResults(results)
        lastSearchStateRef.current = results.length === 0 ? "empty" : "results"
      } catch (err) {
        if (err.name === "AbortError") return
        if (thisController !== abortControllerRef.current) return
        setSearchError("Journal search failed. Please try again.")
        lastSearchStateRef.current = "error"
      } finally {
        if (thisController !== abortControllerRef.current) return
        setIsSearching(false)
      }
    },
    [userToken]
  )

  const handleRetrySearch = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      executeSearch(searchQuery.trim())
    }
  }, [searchQuery, executeSearch])

  function handleSearchQueryChange(e) {
    const value = e.target.value
    setSearchQuery(value)
    setSearchError(null)

    // Clear previous debounce
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

    if (value.trim().length < 2) {
      setSearchResults([])
      setIsSearching(false)
      lastSearchStateRef.current = null
      return
    }

    debounceTimerRef.current = setTimeout(() => {
      executeSearch(value.trim())
    }, 400)
  }

  // ── Journal selection (multi-select) ────────────────────────────────────────
  function journalKey(journal) {
    return journal.issn ?? journal.name ?? journal.title
  }

  function handleSelectJournal(journal) {
    setSelectedJournals((prev) =>
      prev.some((j) => journalKey(j) === journalKey(journal)) ? prev : [...prev, journal]
    )
    setSearchResults([])
    setSearchQuery("")
    setSearchError(null)
    lastSearchStateRef.current = null
  }

  function handleRemoveJournal(journal) {
    setSelectedJournals((prev) => prev.filter((j) => journalKey(j) !== journalKey(journal)))
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  function handleSubmit() {
    onSubmit({
      files: stagedFiles,
      journals: selectedJournals,
      additionalContext,
    })
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const showResultsDropdown =
    !isSearching &&
    !searchError &&
    searchResults.length > 0

  const showEmptyState =
    !isSearching &&
    !searchError &&
    lastSearchStateRef.current === "empty" &&
    searchQuery.trim().length >= 2

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-lg" data-testid="generate-review-dialog">
        <DialogHeader>
          <DialogTitle>Generate Review</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Documents summary */}
          {stagedFiles.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Documents to review:</p>
              <p className="text-sm font-medium truncate">
                {stagedFiles.map((f) => f.name ?? f.filename ?? "Unknown file").join(", ")}
              </p>
            </div>
          )}

          {/* Journal search */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Target Journals <span className="text-muted-foreground font-normal">(optional, multiple allowed)</span>
            </label>

            {/* Selected journal chips */}
            {selectedJournals.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="selected-journals-list">
                {selectedJournals.map((journal, idx) => (
                  <div
                    key={journalKey(journal) ?? idx}
                    className="flex items-center gap-1.5 rounded-md border border-border bg-muted px-3 py-1.5 max-w-full"
                  >
                    <span className="text-sm truncate max-w-xs">{journal.name ?? journal.title}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveJournal(journal)}
                      className="shrink-0 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                      aria-label={`Remove ${journal.name ?? journal.title}`}
                      data-testid="remove-journal-btn"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search input — stays visible so more journals can be added */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search journals..."
                value={searchQuery}
                onChange={handleSearchQueryChange}
                className="pl-8"
                data-testid="journal-search-input"
              />
              {isSearching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Results dropdown */}
            {showResultsDropdown && (
              <div
                className="rounded-md border border-border bg-background shadow-md overflow-y-auto max-h-48"
                data-testid="journal-results-list"
              >
                {searchResults
                  .filter((journal) => !selectedJournals.some((j) => journalKey(j) === journalKey(journal)))
                  .map((journal, idx) => (
                    <button
                      key={journalKey(journal) ?? idx}
                      type="button"
                      className="bg-transparent w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-b border-border last:border-b-0 focus:outline-none focus:bg-muted"
                      onClick={() => handleSelectJournal(journal)}
                      data-testid={`journal-result-${idx}`}
                    >
                      <span className="font-medium">{journal.name ?? journal.title}</span>
                      {journal.publisher && (
                        <span className="text-muted-foreground text-xs block truncate">{journal.publisher}</span>
                      )}
                    </button>
                  ))}
              </div>
            )}

            {/* Empty state */}
            {showEmptyState && (
              <p className="text-sm text-muted-foreground" data-testid="no-journals-message">
                No journals found for this query.
              </p>
            )}

            {/* Error state */}
            {searchError && (
              <div
                className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                data-testid="journal-search-error"
              >
                <AlertCircle className="size-4 shrink-0" />
                <span className="flex-1">{searchError}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={handleRetrySearch}
                  aria-label="Retry search"
                  data-testid="retry-search-btn"
                >
                  <RefreshCw className="size-3" />
                </Button>
              </div>
            )}
          </div>

          {/* Additional context */}
          <div className="flex flex-col gap-2">
            <label htmlFor="additional-context" className="text-sm font-medium">
              Additional context <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Textarea
              id="additional-context"
              placeholder="e.g., Focus on the methods section, use APA citation style..."
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              className="min-h-24 resize-none"
              data-testid="additional-context-textarea"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            data-testid="generate-review-submit-btn"
          >
            Generate Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
